import { PhoneCall, ClipboardCheck, Map, LifeBuoy } from "lucide-react";
import { useLeadModal } from "../../context/LeadModalContext";
import { Reveal, SectionHeading } from "../Reveal";

const STEPS = [
  {
    icon: PhoneCall,
    title: "Book a Free Consultation",
    copy: "A 30-minute call to understand where you are, where you want to be, and what's standing between the two.",
  },
  {
    icon: ClipboardCheck,
    title: "Personalized Assessment",
    copy: "We map your background, strengths, constraints, and timeline into a clear picture of your real options.",
  },
  {
    icon: Map,
    title: "Custom Roadmap",
    copy: "You receive a step-by-step plan — programs, deadlines, documents, and milestones — built only for you.",
  },
  {
    icon: LifeBuoy,
    title: "Ongoing Support",
    copy: "Regular check-ins, expert answers within 24 hours, and course-corrections until you reach the goal.",
  },
];

export function HowItWorks() {
  const { openLeadForm } = useLeadModal();

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="bg-bone py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          chapter={["03", "The Process"]}
          title={
            <>
              From confused to committed in{" "}
              <em className="font-editorial font-medium italic text-gold-dark">four steps.</em>
            </>
          }
          subtitle="A transparent process with no surprises. You always know what happens next, what it costs, and why."
        />

        <div className="relative mt-16">
          <div className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent lg:block" />
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-testid="steps-grid">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12} className="relative">
                <div data-testid={`step-${i + 1}`} className="group">
                  <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl border border-gold/40 bg-white shadow-sm transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_12px_30px_rgba(212,175,55,0.25)]">
                    <s.icon className="h-6 w-6 text-forest" />
                  </div>
                  <p className="mt-6 font-editorial text-5xl font-black text-forest/10 transition-colors duration-300 group-hover:text-gold/30">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-2 font-display text-lg font-bold text-forest-deep">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.25} className="mt-14 text-center">
          <button
            data-testid="how-it-works-cta"
            onClick={() => openLeadForm("how_it_works_section")}
            className="rounded-full bg-forest px-8 py-4 font-display text-base font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(19,78,58,0.35)]"
          >
            Start with Step One — It's Free
          </button>
        </Reveal>
      </div>
    </section>
  );
}
